/** @jsx jsx */
import React, { useState, useRef } from "react";
import { jsx, css } from "@emotion/core";

import {
    style as primaryCTAStyle,
    disabledStyle as disabledPrimaryCTAStyle,
    secondaryColorsStyle,
} from "../../components/PrimaryCTA";

import { Tick } from "../../components/CheckList";
import { colors, borders } from "../../theme";

import fileIcon from "../../assets/file-icon.svg";

const filePickerStyle = css`
    border: ${borders.width} solid ${colors.neutral3};
    border-radius: ${borders.radius};
    width: 100%;
    padding: 5%;
    > div {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
    > div p {
        color: ${colors.neutral6};
        margin-bottom: 0;
    }
    > button {
        margin-top: 24px;
        font-size: 20px;
        padding: 12px;
    }
`;

const removeButtonStyle = disabled => css`
    color: ${disabled ? colors.neutral5 : colors.info5};
    margin-left: auto;
    padding: 0;
    border: none;
    font: inherit;
    background-color: transparent;
    cursor: pointer;
    font-size: 14px;
    font-weight: normal;
    &:focus {
        outline: 0;
    }
    & > * {
        vertical-align: bottom;
    }
    & svg {
        margin-right: 5px;
    }
`;

const pickerLabelStyles = disabled => css`
    &&& {
        color: ${disabled ? colors.neutral2 : colors.primary6};
        background-color: ${colors.neutral0};
        border: 1px solid ${disabled ? colors.neutral2 : colors.primary6};
        display: block;
        margin-top: 24px;
        font-size: 20px;
        padding: 12px;
        &:hover {
            color: ${colors.neutral0};
            background-color: ${colors.primary6};
        }
    }
`;

const chosenFileNameStyles = css`
    margin-top: 24px;
    & p {
        text-decoration: underline;
    }
`;

const FileIcon = ({ size, backgroundColor }) => (
    <div
        css={css`
            border-radius: 100%;
            padding: 0 ${size - 27}px;
            background-color: ${backgroundColor};
            margin-right: 16px;
            height: ${size}px;
            width: ${size}px;
        `}
    >
        <img
            src={fileIcon}
            alt="File"
            css={css`
                height: ${size - 2}px;
                width: ${size - 14}px;
                margin-right: ${size - 27}px;
            `}
        />
    </div>
);

const NoFileChosen = () => (
    <div>
        <FileIcon size={34} backgroundColor={colors.neutral3} />
        <p>No file chosen</p>
    </div>
);

const FileChosen = ({ clickHandler, chosenFile, disabled }) => (
    <>
        <div>
            <Tick size={34} />
            <p>File chosen</p>
        </div>
        <div css={chosenFileNameStyles}>
            <FileIcon size={34} backgroundColor="white" />
            <p data-testid="chosen-file-name">{chosenFile.name}</p>
        </div>
    </>
);

const FileInput = ({ chosenFile, fileInputRef, setFile, id, disabled }) => (
    <>
        <label
            css={[
                disabled ? disabledPrimaryCTAStyle : secondaryColorsStyle,
                pickerLabelStyles(disabled),
            ]}
            htmlFor={id}
        >
            {chosenFile ? "Replace file" : "Choose file"}
        </label>
        <input
            ref={fileInputRef}
            disabled={disabled}
            id={id}
            data-testid={id}
            css={css`
                opacity: 0;
            `}
            type="file"
            name="file"
            onChange={e => {
                e.target.files[0] && setFile(e.target.files[0]);
                e.target.value = "";
                // ^^ to force onChange when user changes to same file
            }}
        />
    </>
);

const FilePicker = ({ chosenFile, setFile, id, disabled }) => {
    const fileInputRef = useRef();
    const clickFileInput = () => fileInputRef.current.click();
    return (
        <div css={filePickerStyle}>
            {chosenFile === null ? (
                <NoFileChosen />
            ) : (
                <FileChosen
                    clickHandler={() => {
                        setFile(null);
                        clickFileInput();
                    }}
                    chosenFile={chosenFile}
                    disabled={disabled}
                />
            )}
            <FileInput
                id={id}
                disabled={disabled}
                chosenFile={chosenFile}
                fileInputRef={fileInputRef}
                setFile={setFile}
                css={css`
                    &&& {
                        /* Necessary to use display: none so that input stays */
                        /* in the DOM to be interacted with */
                        display: ${chosenFile === null ? "block" : "none"};
                    }
                `}
            />
        </div>
    );
};

export default FilePicker;
